import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { venueImages } from '../../constants/realVenueData';
import { getMetroDistanceText } from '../../services/metroDistanceService';
import venueApiService from '../../services/venueApi';
import VenueHeader from './VenueHeader';
import VenueImageGallery from './VenueImageGallery';
import VenueGalleryModal from './VenueGalleryModal';
import VenueHostInfo from './VenueHostInfo';
import VenueBookingSidebar from './VenueBookingSidebar';
import VenueAmenities, { isKasaVenue, KasaBookingOptions } from './VenueAmenities';
import VenueCommunity from './VenueCommunity';
import { BuildingIcon, CloseIcon, UserIcon } from '../icons';
import VenueAboutInfo from './venueAboutInfo';
import VenueSpacesSection from './VenueSpacesSection';
import { normalizeVenueSpacesForDisplay } from '../../utils/venueSpacesDisplay';
import './VenueDetails.css';
import { getYmdInTimezone } from '../VenueDashboard/utils/dateUtils';
import {
  buildVenuePath,
  getVenueSlug,
  isVenueUuid,
  resolveVenueIdFromPathParam,
} from '../../utils/venueSlug';
import {
  buildVenueDiscoveryBundle,
  fetchVenueDiscoveryBundle,
  getCachedVenueDiscoveryBundle,
  setCachedVenueDiscoveryBundle,
  venueIdsMatch
} from '../../utils/venueDiscoveryCache';
import { isPerPersonBookingEnabled } from '../../utils/bookingPricingMetadata';
import { resolveVenueAlcoholSettings } from '../../utils/bookingHostRules';
import { usePageSeo, buildVenueListingJsonLd } from '../Seo/PageSeo';
import { SITE_ORIGIN } from '../../utils/seoConstants';

const VenueDetails = () => {
  const { venueId: venuePathParam } = useParams();
  const [resolvedVenueId, setResolvedVenueId] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const routeVenueSummary = location.state?.venueSummary ?? null;
  const [searchParams] = useSearchParams();
  const [venue, setVenue] = useState(null);
  const [venueSpaces, setVenueSpaces] = useState([]);
  const [operatingHours, setOperatingHours] = useState({});
  const [operatingHoursPerPerson, setOperatingHoursPerPerson] = useState({});
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sidebarLoading, setSidebarLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dynamicPricing, setDynamicPricing] = useState([]);
  const [defaultPerPersonPrice, setDefaultPerPersonPrice] = useState(null);
  const [dynamicPricingPerPerson, setDynamicPricingPerPerson] = useState([]);
  const [flatPricing, setFlatPricing] = useState([]);
  const [selectedDateCoverage, setSelectedDateCoverage] = useState(null);
  const [bookingRules, setBookingRules] = useState(null);
  const [reviewCount, setReviewCount] = useState(null);

  // Initialize selectedDate from URL search parameters, fallback to today
  const getInitialDate = () => {
    const urlDate = searchParams.get('date');

    if (urlDate) {
      const isoDateRegex = /^\d{4}-\d{2}-\d{2}$/;
      if (isoDateRegex.test(urlDate)) {
        return urlDate;
      }
    }

    // "Today" for default selection uses UK civil date, not UTC (toISOString), so it matches venue-side booking.
    return getYmdInTimezone(new Date(), 'Europe/London');
  };

  const [selectedDate, setSelectedDate] = useState(getInitialDate());
  const [startTime, setStartTime] = useState('18:00');
  const [endTime, setEndTime] = useState('22:00');
  const [isFlexible, setIsFlexible] = useState(true);
  const [showAllPhotos, setShowAllPhotos] = useState(false);
  const [bookingSheetOpen, setBookingSheetOpen] = useState(false);
  const [galleryInitialIndex, setGalleryInitialIndex] = useState(0);
  const [isStartTimeOpen, setIsStartTimeOpen] = useState(false);
  const [isEndTimeOpen, setIsEndTimeOpen] = useState(false);

  useEffect(() => {
    if (!bookingSheetOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setBookingSheetOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [bookingSheetOpen]);

  // Helper function to calculate duration in hours
  const calculateDuration = (startTime, endTime) => {
    const [startHour, startMin] = startTime.split(':').map(Number);
    const [endHour, endMin] = endTime.split(':').map(Number);

    const startMinutes = startHour * 60 + startMin;
    const endMinutes = endHour * 60 + endMin;

    let durationMinutes = endMinutes - startMinutes;
    if (durationMinutes < 0) {
      durationMinutes += 24 * 60;
    }

    return durationMinutes / 60;
  };

  // Helper function to calculate distance from closest metro station
  const calculateDistance = (venue) => {
    if (!venue) {
      return 'Transport info not available';
    }
    return getMetroDistanceText(venue);
  };

  // Helper function to get fallback venue image
  const getVenueImage = (index) => {
    const venueIdNum = parseInt(venue?.id || 0);
    const imageIndex = (venueIdNum + index) % venueImages.length;
    return venueImages[imageIndex];
  };

  // Helper function to get all venue images
  const getAllVenueImages = (venue) => {
    if (venue && venue.images && venue.images.length > 0) {
      return venue.images
        .sort((a, b) => {
          if (a.is_primary && !b.is_primary) return -1;
          if (!a.is_primary && b.is_primary) return 1;
          return new Date(b.uploaded_at) - new Date(a.uploaded_at);
        })
        .map(img => ({
          src: img.image_url || img.url || img.src,
          caption: img.caption || img.description || '',
          is_primary: img.is_primary,
          uploaded_at: img.uploaded_at
        }));
    }
    return [];
  };

  // Get all images for the current venue
  const allImages = venue ? getAllVenueImages(venue) : [];

  // Map API venue.amenities (+ top-level facilities / cancellation_policy) for VenueAmenities
  const getAmenitiesData = (venue) => {
    const empty = {
      foodBeverage: {
        inHouseCatering: [],
        externalCatering: [],
        alcohol: { venueProvides: false, byoAllowed: false, corkageFee: false }
      },
      eventRules: {
        allowedEvents: [],
        hostRules: [],
        cancellationPolicy: 'Contact venue'
      },
      amenities: [],
      parking: [],
      spaces: []
    };

    if (!venue) return empty;

    const a = venue.amenities || {};
    const fb = a.foodBeverage || a.food_beverage || {};
    const er = a.eventRules || a.event_rules || {};

    const cancellationPolicy =
      (typeof er.cancellationPolicy === 'string' && er.cancellationPolicy.trim()) ||
      (typeof er.cancellation_policy === 'string' && er.cancellation_policy.trim()) ||
      (typeof venue.cancellation_policy === 'string' && venue.cancellation_policy.trim()) ||
      (typeof venue.cancellationPolicy === 'string' && venue.cancellationPolicy.trim()) ||
      'Contact venue';

    const alcoholSettings = resolveVenueAlcoholSettings(venue);
    const foodBeverage = {
      inHouseCatering: Array.isArray(fb.inHouseCatering) ? fb.inHouseCatering : [],
      externalCatering: Array.isArray(fb.externalCatering) ? fb.externalCatering : [],
      alcohol: alcoholSettings
    };

    const hostRulesRaw = er.hostRules ?? er.host_rules ?? [];
    const eventRules = {
      allowedEvents: Array.isArray(er.allowedEvents) ? er.allowedEvents : [],
      hostRules: Array.isArray(hostRulesRaw) ? hostRulesRaw : [],
      cancellationPolicy
    };

    let amenitiesGrid = [];
    if (Array.isArray(a.amenities) && a.amenities.length > 0) {
      amenitiesGrid = a.amenities.map((item) => {
        if (typeof item === 'string') return { name: item, available: true };
        return {
          name: item.name || item.label || '',
          available: item.available !== false
        };
      });
    } else if (Array.isArray(venue.facilities) && venue.facilities.length > 0) {
      amenitiesGrid = venue.facilities.map((name) => ({ name, available: true }));
    }

    const parking = Array.isArray(a.parking) ? a.parking : [];

    return {
      foodBeverage,
      eventRules,
      amenities: amenitiesGrid,
      parking,
      spaces: venue.spaces || []
    };
  };

  // Compute selectedDateCoverage when selectedDate or dynamicPricing changes (e.g. from URL)
  useEffect(() => {
    if (!selectedDate || !dynamicPricing?.length) {
      setSelectedDateCoverage(null);
      return;
    }
    const [y, m, d] = selectedDate.split('-').map(Number);
    const compareDate = new Date(y, m - 1, d);
    const applicableRules = dynamicPricing.filter(rule => {
      if (!rule.start_date || !rule.end_date) return false;
      const [sY, sM, sD] = rule.start_date.split('-').map(Number);
      const [eY, eM, eD] = rule.end_date.split('-').map(Number);
      const startDate = new Date(sY, sM - 1, sD);
      const endDate = new Date(eY, eM - 1, eD);
      return compareDate >= startDate && compareDate <= endDate;
    });
    if (applicableRules.length === 0) {
      setSelectedDateCoverage(null);
      return;
    }
    const totalHours = applicableRules.reduce((total, rule) => {
      if (!rule.start_time && !rule.end_time) return total + 24;
      if (rule.start_time === '00:00:00' && rule.end_time === '00:00:00') return total + 24;
      if (rule.start_time && rule.end_time) {
        const startT = new Date(`2000-01-01T${rule.start_time}`);
        const endT = new Date(`2000-01-01T${rule.end_time}`);
        let duration = (endT - startT) / (1000 * 60 * 60);
        if (duration < 0) duration += 24;
        return total + duration;
      }
      return total + 24;
    }, 0);
    setSelectedDateCoverage(totalHours > 23 ? 'all_hours' : 'select_hours');
  }, [selectedDate, dynamicPricing]);

  useEffect(() => {
    let cancelled = false;

    const resolveVenueRoute = async () => {
      setResolvedVenueId(null);
      setError(null);

      try {
        const id = await resolveVenueIdFromPathParam(venuePathParam, venueApiService);
        if (cancelled) return;

        if (!id) {
          setError('Venue not found');
          setLoading(false);
          return;
        }

        setResolvedVenueId(id);
      } catch (err) {
        if (!cancelled) {
          setError(err.message);
          setLoading(false);
        }
      }
    };

    resolveVenueRoute();

    return () => {
      cancelled = true;
    };
  }, [venuePathParam]);

  // Fetch venue data — paint main content after venue record; sidebar data loads progressively
  useEffect(() => {
    if (!resolvedVenueId) return;

    let cancelled = false;

    const applyDiscoveryBundle = (bundle) => {
      if (!bundle) return;
      if (bundle.venueData) setVenue(bundle.venueData);
      setBookingRules(bundle.bookingRulesData ?? null);
      setVenueSpaces(normalizeVenueSpacesForDisplay(bundle.spacesData).map((s) => s.raw));
      setOperatingHours(
        typeof bundle.operatingHoursData === 'object' && bundle.operatingHoursData !== null
          ? bundle.operatingHoursData
          : {}
      );
      setOperatingHoursPerPerson(
        typeof bundle.operatingHoursPerPersonData === 'object' &&
          bundle.operatingHoursPerPersonData !== null
          ? bundle.operatingHoursPerPersonData
          : {}
      );
      setHolidays(Array.isArray(bundle.holidaysData) ? bundle.holidaysData : []);
      setFlatPricing(Array.isArray(bundle.flatPricingData) ? bundle.flatPricingData : []);
      setDefaultPerPersonPrice(bundle.defaultPerPersonData?.default_per_person_price ?? null);
      setDynamicPricingPerPerson(
        Array.isArray(bundle.dynamicPerPersonData) ? bundle.dynamicPerPersonData : []
      );
      if (Array.isArray(bundle.pricing) && bundle.pricing.length > 0) {
        setDynamicPricing(bundle.pricing);
      } else if (Array.isArray(bundle.pricing)) {
        setDynamicPricing([]);
      }
    };

    const fetchVenueData = async () => {
      const cached = getCachedVenueDiscoveryBundle(resolvedVenueId);
      if (cached?.venueData) {
        applyDiscoveryBundle(cached);
        setLoading(false);
        setSidebarLoading(false);
        try {
          const freshVenue = await venueApiService.getVenueById(resolvedVenueId, {
            forceRefresh: true
          });
          if (cancelled) return;
          setVenue(freshVenue);
          const bundle = await fetchVenueDiscoveryBundle(resolvedVenueId, {
            venueData: freshVenue
          });
          if (cancelled) return;
          applyDiscoveryBundle(bundle);
          setCachedVenueDiscoveryBundle(resolvedVenueId, bundle);
        } catch {
          // Keep cached paint if background refresh fails.
        }
        return;
      }

      const summary =
        venueIdsMatch(routeVenueSummary?.id, resolvedVenueId) ? routeVenueSummary : null;
      if (summary) {
        setVenue(summary);
        setLoading(false);
        setSidebarLoading(true);
      } else {
        setLoading(true);
        setSidebarLoading(false);
      }

      try {
        setError(null);
        const venueData = await venueApiService.getVenueById(resolvedVenueId);
        if (cancelled) return;

        setVenue(venueData);
        setLoading(false);
        setSidebarLoading(true);

        const bundle = await fetchVenueDiscoveryBundle(resolvedVenueId, { venueData });
        if (cancelled) return;

        applyDiscoveryBundle(bundle);
        setCachedVenueDiscoveryBundle(resolvedVenueId, bundle);
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) {
          setLoading(false);
          setSidebarLoading(false);
        }
      }
    };

    fetchVenueData();

    return () => {
      cancelled = true;
    };
  }, [resolvedVenueId, routeVenueSummary]);

  useEffect(() => {
    if (!venue || !venuePathParam) return;

    const canonicalSlug = getVenueSlug(venue);
    const currentParam = String(venuePathParam).trim().toLowerCase();
    const shouldCanonicalize =
      isVenueUuid(venuePathParam) || currentParam !== canonicalSlug;

    if (!shouldCanonicalize) return;

    const queryString = searchParams.toString();
    navigate(buildVenuePath(venue, { searchParams: queryString }), { replace: true });
  }, [venue, venuePathParam, searchParams, navigate]);

  // Handler functions
  const handleImageClick = (_image, index) => {
    setGalleryInitialIndex(index);
    setShowAllPhotos(true);
  };

  const handleShowAllPhotos = () => {
    setGalleryInitialIndex(0);
    setShowAllPhotos(true);
  };

  const toggleStartTime = () => {
    setIsStartTimeOpen(!isStartTimeOpen);
  };

  const toggleEndTime = () => {
    setIsEndTimeOpen(!isEndTimeOpen);
  };

  const handleBookNow = (bookingData = {}) => {
    const params = new URLSearchParams({
      date: bookingData.date ?? selectedDate,
      startTime: bookingData.startTime ?? startTime,
      endTime: bookingData.endTime ?? endTime
    });
    const venueBriefRequestId = searchParams.get('venueBriefRequestId');
    if (venueBriefRequestId) {
      params.set('venueBriefRequestId', venueBriefRequestId);
    }
    if (bookingData.spaceId) params.set('spaceId', bookingData.spaceId);
    if (bookingData.bookingType) params.set('bookingType', bookingData.bookingType);
    if (bookingData.tableCount != null) params.set('tableCount', String(bookingData.tableCount));
    navigate(`/booking/${resolvedVenueId}?${params.toString()}`, {
      state: {
        discoveryBundle: buildVenueDiscoveryBundle({
          venueData: venue,
          pricing: dynamicPricing,
          spacesData: venueSpaces,
          operatingHoursData: operatingHours,
          operatingHoursPerPersonData: operatingHoursPerPerson,
          holidaysData: holidays,
          flatPricingData: flatPricing,
          defaultPerPersonData: defaultPerPersonPrice != null
            ? { default_per_person_price: defaultPerPersonPrice }
            : null,
          dynamicPerPersonData: dynamicPricingPerPerson,
          bookingRulesData: bookingRules
        })
      }
    });
  };

  const venueSeoPath = useMemo(() => {
    if (venue) return buildVenuePath(venue);
    if (venuePathParam) return `/venue/${venuePathParam}`;
    return '/search';
  }, [venue, venuePathParam]);

  const venueSeoDescription = useMemo(() => {
    if (!venue) return undefined;
    const raw = (venue.description || venue.tagline || '')
      .replace(/<[^>]+>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    const cityBit = venue.city ? ` in ${venue.city}` : '';
    if (raw.length >= 80) return raw.slice(0, 155);
    return `Book ${venue.name}${cityBit} for private hire, workshops, and events. View capacity, photos, and availability on Occasia.`;
  }, [venue]);

  const venueSeoImage = useMemo(() => {
    const url = venue?.primary_image_url || venue?.primaryImageUrl;
    if (!url) return undefined;
    return url.startsWith('http') ? url : `${SITE_ORIGIN}${url.startsWith('/') ? url : `/${url}`}`;
  }, [venue]);

  usePageSeo({
    title: venue
      ? `${venue.name}${venue.city ? `, ${venue.city}` : ''}`
      : 'Venue details',
    description: venueSeoDescription,
    path: venueSeoPath,
    image: venueSeoImage,
    jsonLd: venue ? buildVenueListingJsonLd(venue) : null
  });

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-content">
          <div className="loading-spinner"></div>
          <h2>Loading venue details...</h2>
          <p>Please wait while we fetch the venue information.</p>
        </div>
      </div>
    );
  }

  if (error || !venue) {
    return (
      <div className="error-container">
        <p>Error loading venue: {error || 'Venue not found'}</p>
        <button onClick={() => navigate('/search')}>Back to Search</button>
      </div>
    );
  }

  const perPersonBookingOffered = isPerPersonBookingEnabled(bookingRules);

  return (
    <div className="venue-details-page" style={{ overflow: 'visible', position: 'relative' }}>
      <div className="vd-main">
      <VenueHeader
        venue={venue}
        calculateDistance={calculateDistance}
        reviewCount={reviewCount}
      />

      {/* Main Content */}
      <div className="vd-body">
        <div>
          <VenueImageGallery
            venue={venue}
            allImages={allImages}
            getVenueImage={getVenueImage}
            onImageClick={handleImageClick}
            onShowAllPhotos={handleShowAllPhotos}
          />

          <section className="flex flex-col lg:flex-row gap-8 justify-between">
            <div className="venue-content-main flex-1">
              <VenueHostInfo venue={venue} />
              {/* About Section */}
              <VenueAboutInfo description={venue.description} />
              <section className="venue-booking-options">
                <h3 className="venue-section-title">Booking options</h3>
                {isKasaVenue(venue) ? (
                  <KasaBookingOptions />
                ) : (
                <div className="booking-options-grid">
                  {perPersonBookingOffered ? (
                    <div className="booking-option-card">
                      <div className="booking-option-icon">
                        <UserIcon />
                      </div>
                      <div className="booking-option-text">
                        <span className="booking-option-title">Per person</span>
                        <span className="booking-option-sub">During operating hours</span>
                      </div>
                    </div>
                  ) : null}
                  <div className="booking-option-card">
                    <div className="booking-option-icon">
                      <BuildingIcon />
                    </div>
                    <div className="booking-option-text">
                      <span className="booking-option-title">Full venue hire</span>
                      <span className="booking-option-sub">During and outside operating hours</span>
                    </div>
                  </div>
                </div>
                )}
              </section>
              <VenueSpacesSection spaces={venueSpaces} />
              {/* Amenities Sections */}
              <VenueAmenities
                venue={venue}
                amenitiesData={getAmenitiesData(venue)}
                operatingHours={operatingHours}
              />
              <VenueCommunity
                venueId={venue.id}
                images={allImages}
                rating={venue.rating}
                onReviewCountChange={setReviewCount}
              />
            </div>

          </section>
        </div>
      </div>
      </div>

      {bookingSheetOpen ? (
        <button
          type="button"
          className="vd-booking-sheet-backdrop"
          aria-label="Close"
          onClick={() => setBookingSheetOpen(false)}
        />
      ) : null}
      <aside
        className={`vd-booking-panel${bookingSheetOpen ? ' is-open' : ''}`}
        role={bookingSheetOpen ? 'dialog' : undefined}
        aria-modal={bookingSheetOpen ? 'true' : undefined}
        aria-label={bookingSheetOpen ? 'Book this venue' : undefined}
      >
        <div className="vd-booking-card">
        <div className="vd-booking-sheet-bar">
          <h2 className="vd-panel-title">Book this venue</h2>
          <button
            type="button"
            className="vd-booking-sheet-close"
            aria-label="Close"
            onClick={() => setBookingSheetOpen(false)}
          >
            <CloseIcon />
          </button>
        </div>
        <VenueBookingSidebar
          venue={venue}
          spaces={venueSpaces}
          operatingHours={operatingHours}
          operatingHoursPerPerson={operatingHoursPerPerson}
          holidays={holidays}
          sidebarDataLoading={sidebarLoading}
          selectedDate={selectedDate}
          selectedDateCoverage={selectedDateCoverage}
          startTime={startTime}
          endTime={endTime}
          isFlexible={isFlexible}
          dynamicPricing={dynamicPricing}
          defaultPerPersonPrice={defaultPerPersonPrice}
          dynamicPricingPerPerson={dynamicPricingPerPerson}
          flatPricing={flatPricing}
          bookingRules={bookingRules}
          isStartTimeOpen={isStartTimeOpen}
          isEndTimeOpen={isEndTimeOpen}
          onDateSelect={(date, coverage) => {
            setSelectedDate(date);
            setSelectedDateCoverage(coverage);
          }}
          onStartTimeChange={(e) => setStartTime(e.target.value)}
          onEndTimeChange={(e) => setEndTime(e.target.value)}
          onFlexibleChange={(e) => setIsFlexible(e.target.checked)}
          onBookNow={handleBookNow}
          toggleStartTime={toggleStartTime}
          toggleEndTime={toggleEndTime}
          calculateDuration={calculateDuration}
        />
        </div>
      </aside>

      <div className={`vd-booking-launch${bookingSheetOpen ? ' is-hidden' : ''}`}>
        <button type="button" className="book-button" onClick={() => setBookingSheetOpen(true)}>
          Book this venue
        </button>
      </div>

      <VenueGalleryModal
        show={showAllPhotos}
        images={allImages}
        initialIndex={galleryInitialIndex}
        onClose={() => setShowAllPhotos(false)}
      />
    </div>
  );
};

export default VenueDetails;
